/**
 * User Action Tracking Utility
 * Tracks user actions and stores them in localStorage for audit purposes
 */

export type UserActionType =
  | "Query Selected for assigning current call"
  | "Shared Quotation for Query"
  | "Added Itinerary for Query"
  | "New Query Created"
  | "Existing Query Viewed";

  

export interface UserAction {
  action: UserActionType;
  query_id: string;
  user: string;
  timestamp: number;
  sessionId?: string;
}

const STORAGE_KEY = "callsLogModal_userActionHistory";
const MAX_ACTIONS = 100; // Keep last 100 actions

/**
 * Verify localStorage is working
 */
const verifyStorageAccess = (): boolean => {
  try {
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, "test");
    const testValue = localStorage.getItem(testKey);
    localStorage.removeItem(testKey);
    return testValue === "test";
  } catch {
    return false;
  }
};

/**
 * Track a user action to localStorage
 * @param action - Type of action performed
 * @param queryId - Query ID associated with the action
 * @param user - Username of the user performing the action
 * @param sessionId - Optional session ID from call
 */
export const trackUserAction = (
  action: UserActionType,
  queryId: string,
  user: string,
  sessionId?: string
): void => {
  try {
    // Verify storage access first
    if (!verifyStorageAccess()) {
      console.error("localStorage is not accessible");
      return;
    }

    // Get existing actions
    const stored = localStorage.getItem(STORAGE_KEY);
    const actions: UserAction[] = stored ? JSON.parse(stored) : [];

    // Create new action
    const newAction: UserAction = {
      action,
      query_id: queryId,
      user,
      timestamp: Date.now(),
      sessionId,
    };

    // Add to history
    actions.push(newAction);

    // Keep only recent actions (max MAX_ACTIONS)
    if (actions.length > MAX_ACTIONS) {
      actions.splice(0, actions.length - MAX_ACTIONS);
    }

    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(actions));

    // Debug log in development
    if (process.env.NODE_ENV !== "production") {
      console.debug("User Action Tracked:", newAction);
      console.log("Current Actions in localStorage:", localStorage.getItem(STORAGE_KEY));
    }
  } catch (error) {
    console.error("Error tracking user action:", error);
  }
};

/**
 * Get all tracked user actions
 */
export const getUserActionHistory = (): UserAction[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error("Error retrieving user action history:", error);
    return [];
  }
};

/**
 * Get actions for a specific query
 */
export const getQueryActions = (queryId: string): UserAction[] => {
  const history = getUserActionHistory();
  return history.filter((action) => action.query_id === queryId);
};

/**
 * Get actions for a specific user
 */
export const getUserActions = (user: string): UserAction[] => {
  const history = getUserActionHistory();
  return history.filter((action) => action.user === user);
};

/**
 * Get actions for a specific session
 */
export const getSessionActions = (sessionId: string): UserAction[] => {
  const history = getUserActionHistory();
  return history.filter((action) => action.sessionId === sessionId);
};

/**
 * Clear action history
 */
export const clearActionHistory = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    if (process.env.NODE_ENV !== "production") {
      console.debug("User action history cleared");
    }
  } catch (error) {
    console.error("Error clearing action history:", error);
  }
};

/**
 * Get actions within a time range
 */
export const getActionsByDateRange = (
  startTime: number,
  endTime: number
): UserAction[] => {
  const history = getUserActionHistory();
  return history.filter(
    (action) => action.timestamp >= startTime && action.timestamp <= endTime
  );
};

/**
 * Export action history as JSON
 */
export const exportActionHistory = (): string => {
  const history = getUserActionHistory();
  return JSON.stringify(history, null, 2);
};

/**
 * Get the storage key being used
 */
export const getStorageKey = (): string => {
  return STORAGE_KEY;
};
